/**
 * BookSettings — collapsible book configuration panel.
 *
 * Phase 10: D-01–D-06, D-14, D-28–D-30
 * Always visible above Generate button. Collapsed by default after initial setup.
 */
import { useState } from 'react';
import { fetchWithAuth } from '../lib/api';

export interface BookConfig {
  voice: 'first' | 'third';
  tone: 'warm' | 'literary' | 'lighthearted';
  title: string;
  dedication: string;
  includeToc: boolean;
  includeIndex: boolean;
}

export const DEFAULT_BOOK_CONFIG: BookConfig = {
  voice: 'first',
  tone: 'warm',
  title: "Gary's Memoir",
  dedication: '',
  includeToc: true,
  includeIndex: true,
};

const TONE_PRESETS = [
  { id: 'warm' as const, name: 'Warm & Personal', description: 'Conversational, like talking to family' },
  { id: 'literary' as const, name: 'Literary', description: 'Elevated prose, richer vocabulary' },
  { id: 'lighthearted' as const, name: 'Lighthearted', description: "Gary's humor woven in naturally" },
];

interface BookSettingsProps {
  config: BookConfig;
  onChange: (config: BookConfig) => void;
  /** Called with AI-suggested titles after outline pass — Book.tsx passes these in */
  titleSuggestions?: string[];
}

export function BookSettings({ config, onChange, titleSuggestions }: BookSettingsProps) {
  const [expanded, setExpanded] = useState(true); // open on first visit
  const [includeDedication, setIncludeDedication] = useState(!!config.dedication);
  const panelId = 'book-settings-panel';

  const update = (partial: Partial<BookConfig>) => onChange({ ...config, ...partial });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="w-full flex items-center justify-between"
      >
        <span className="text-sm font-semibold text-stone-700">Book Settings</span>
        <div className="flex items-center gap-2">
          {!expanded && (
            <span className="text-xs text-stone-400">
              {TONE_PRESETS.find(t => t.id === config.tone)?.name}
            </span>
          )}
          {/* Chevron icon */}
          <svg
            className={`w-4 h-4 text-stone-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div id={panelId} className="space-y-5 mt-5">

          {/* Book Title */}
          <div>
            <label htmlFor="book-title" className="block text-sm font-semibold text-stone-700 mb-1">
              Book Title
            </label>
            <input
              id="book-title"
              type="text"
              value={config.title}
              onChange={e => update({ title: e.target.value })}
              placeholder="Gary's Memoir"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
            {titleSuggestions && titleSuggestions.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="text-xs text-stone-400">AI suggestions:</span>
                {titleSuggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => update({ title: s })}
                    className="text-xs text-amber-600 hover:text-amber-700 underline-offset-2 hover:underline"
                  >
                    &ldquo;{s}&rdquo;
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Voice toggle */}
          <div>
            <p className="text-sm font-semibold text-stone-700 mb-2">Voice</p>
            <div className="flex gap-2" role="group" aria-label="Voice">
              {(['first', 'third'] as const).map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => update({ voice: v })}
                  aria-pressed={config.voice === v}
                  className={config.voice === v
                    ? 'px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold'
                    : 'px-4 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-50'
                  }
                >
                  {v === 'first' ? 'First Person' : 'Third Person'}
                </button>
              ))}
            </div>
          </div>

          {/* Tone presets */}
          <div>
            <p className="text-sm font-semibold text-stone-700 mb-2">Tone</p>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Tone">
              {TONE_PRESETS.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  role="radio"
                  aria-checked={config.tone === preset.id}
                  onClick={() => update({ tone: preset.id })}
                  className={config.tone === preset.id
                    ? 'border-2 border-amber-500 bg-amber-50 rounded-xl p-3 cursor-pointer text-left'
                    : 'border border-stone-200 bg-white rounded-xl p-3 cursor-pointer hover:border-stone-300 text-left'
                  }
                >
                  <p className="text-sm font-semibold text-stone-800">{preset.name}</p>
                  <p className="text-xs text-stone-500 mt-0.5">{preset.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Front matter toggles */}
          <div>
            <p className="text-sm font-semibold text-stone-700 mb-2">Include</p>
            <div className="space-y-2">
              {[
                { key: 'includeToc' as const, label: 'Table of contents' },
                { key: 'includeIndex' as const, label: 'Index / closing page' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm text-stone-700">{label}</span>
                  <input
                    type="checkbox"
                    checked={config[key] as boolean}
                    onChange={e => update({ [key]: e.target.checked })}
                    className="accent-amber-500 w-4 h-4"
                    aria-label={label}
                  />
                </div>
              ))}
              {/* Dedication toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-700">Dedication page</span>
                <input
                  type="checkbox"
                  checked={includeDedication}
                  onChange={e => {
                    setIncludeDedication(e.target.checked);
                    if (!e.target.checked) update({ dedication: '' });
                  }}
                  className="accent-amber-500 w-4 h-4"
                  aria-label="Dedication page"
                />
              </div>
              {includeDedication && (
                <textarea
                  value={config.dedication}
                  onChange={e => update({ dedication: e.target.value })}
                  placeholder="For Karen, my partner in every adventure"
                  rows={2}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-600 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
                />
              )}
            </div>
          </div>

          {/* Footer hint */}
          <p className="text-xs text-stone-400 mt-4 pt-4 border-t border-stone-100">
            Estimated generation: ~3–8 minutes. Previous version will be replaced.
          </p>
        </div>
      )}
    </div>
  );
}

/** Load saved BookConfig from API, returns DEFAULT_BOOK_CONFIG on error */
export async function loadBookSettings(restUrl: string): Promise<BookConfig> {
  try {
    const res = await fetchWithAuth(`${restUrl}book/settings`);
    if (!res.ok) return { ...DEFAULT_BOOK_CONFIG };
    return await res.json() as BookConfig;
  } catch {
    return { ...DEFAULT_BOOK_CONFIG };
  }
}
